






















































import { Component, Prop, Vue, Watch, Emit } from 'vue-property-decorator';
import AutocompleteField from '@/components/AutocompleteField.vue';
import FacilityItem from '@/components/FacilityItem.vue';
import { Facility } from '@/models/facility';
import { City } from '@/models/city';
import { CityService } from '@/services/city.service';
import { FacilityService } from '@/services/facility.service';
import Header from '@/components/Header.vue';
import { TIMERS } from '@/const/timers';
import debounce from 'lodash/debounce';
import cuid from 'cuid';
import { ANALYTICS_EVENTS } from '@/const/analytics-events';

@Component({
  name: 'select-facility-widget',
  components: {
    AutocompleteField,
    FacilityItem,
    Header,
  },
})
export default class SelectFacilityWidget extends Vue {
  @Prop({ type: String }) title?: string;
  @Prop({ type: String, required: true }) cityLabel!: string;
  @Prop({ type: String, required: true }) cityPlaceholder!: string;
  @Prop({ type: String, required: true }) facilityLabel!: string;
  @Prop({ type: String, required: true }) facilityPlaceholder!: string;
  @Prop({ type: String, required: true }) elementType!: string;

  private city: City | null = null;
  private nextCityPage: number | null = 1;
  private cities: City[] = [];
  private citiesLoading: boolean = false;
  private citiesSearchVal?: string | null = null;

  private facility: Facility | null = null;
  private nextFacilityPage: number | null = 1;
  private facilities: Facility[] = [];
  private facilitiesLoading: boolean = false;
  private facilitiesSearchVal?: string | null = null;

  private debounceCitySearch!: any;
  private debounceFacilitySearch!: any;
  private get isCustomFont() {
    return this.$store.getters.isVox;
  }
  private get cityFieldName(): string {
    return `${this.elementType}${cuid()}`;
  }

  private get facilityFieldName(): string {
    return `${this.elementType}${cuid()}`;
  }

  get surveyId(): string | null {
    return this.$store.state.surveyId;
  }

  private async created() {
    this.debounceCitySearch = debounce(
      query => this.fetchCities(query),
      TIMERS.SEARCH_DEBOUNCE
    );

    this.debounceFacilitySearch = debounce(
      query => this.fetchFacilities(query),
      TIMERS.SEARCH_DEBOUNCE
    );
  }

  private getNextPageNumber(nextUrl: string | null): number | null {
    if (!nextUrl) return null;
    const nextQueryParams = new URL(nextUrl);
    const searchParams = new URLSearchParams(nextQueryParams.search);
    return parseInt(searchParams.get('page') || '0');
  }

  private async fetchCities(searchVal?: string): Promise<void> {
    if (!this.nextCityPage && searchVal == this.citiesSearchVal) return;
    if (searchVal === '') {
      this.nextCityPage = 1;
      this.cities = [];
    }
    this.startCityLoading(searchVal);

    try {
      const surveyId = this.getSurveyId(searchVal);
      const { results, next } = await CityService.getCities(
        searchVal,
        surveyId,
        this.nextCityPage
      );

      this.updateCities(results, next, searchVal);
    } catch (error) {
      this.handleError('Error fetching cities:', error);
    } finally {
      this.citiesLoading = false;
    }
  }
  clearCityData() {
    this.city = null;
    this.cities = [];
    this.nextCityPage = 1;
  }

  clearFacilityData() {
    this.facility = null;
    this.facilities = [];
    this.nextFacilityPage = 1;
  }

  private startCityLoading(searchVal?: string): void {
    if (this.nextCityPage === 1) {
      this.citiesLoading = true;
      this.cities = [];
    }
    if (searchVal && this.citiesSearchVal !== searchVal) {
      this.clearCityData();
    }

    this.citiesSearchVal = searchVal;
  }

  private getSurveyId(searchVal?: string): string | undefined {
    return searchVal ? this.surveyId! : undefined;
  }

  private updateCities(
    results: any[],
    next: string | null,
    searchVal?: string
  ): void {
    this.cities = [...this.cities, ...results];
    this.nextCityPage = this.getNextPageNumber(next);
  }

  private handleError(message: string, error: any): void {
    console.error(message, error);
    throw error;
  }

  private async fetchFacilities(
    searchVal: string | undefined = undefined
  ): Promise<void> {
    if (!this.nextFacilityPage && searchVal == this.facilitiesSearchVal) return;
    if (searchVal === '') {
      this.facilities = [];
      this.nextFacilityPage = 1;
    }
    this.startFacilityLoading(searchVal);

    if (this.city?.name) {
      try {
        const surveyId = this.getSurveyId(searchVal);
        const { results: facility, next } = await FacilityService.getFacilities(
          {
            city: this.city.name,
            address: searchVal,
            survey_id: surveyId,
          },
          this.nextFacilityPage
        );

        this.updateFacilities(facility, next, searchVal);
      } catch (error) {
        this.handleError('Error fetching facilities:', error);
      }
    } else {
      this.facilities = [];
    }

    this.facilitiesLoading = false;
  }

  private startFacilityLoading(searchVal?: string): void {
    if (this.nextFacilityPage === 1) {
      this.facilitiesLoading = true;
      this.facilities = [];
    }
    if (searchVal && this.facilitiesSearchVal !== searchVal) {
      this.clearFacilityData();
    }

    this.facilitiesSearchVal = searchVal;
  }

  private updateFacilities(
    facility: any[],
    next: string | null,
    searchVal?: string
  ): void {
    this.facilities = [...this.facilities, ...facility];
    this.nextFacilityPage = this.getNextPageNumber(next);
  }

  private async cityFocusedHandler() {
    await this.fetchCities();
  }

  private async facilityFocusedHandler() {
    await this.fetchFacilities();
  }

  @Watch('city')
  private async onCityChange(current: City | null, previous: City | null) {
    this.facilitiesLoading = true;
    this.nextCityPage = 1;
    if (current?.name) {
      this.$mixpanel(ANALYTICS_EVENTS.CITY_SELECTED, {
        name: current?.name,
      });
    }
    this.facilities = [];
    this.facility = null;
    this.nextFacilityPage = 1;
    await this.fetchFacilities();
    this.facilitiesLoading = false;
  }

  @Watch('facility')
  @Emit()
  private facilityChange(facility: Facility | null) {
    this.nextFacilityPage = 1;
    if (facility) {
      this.$mixpanel(ANALYTICS_EVENTS.FACILITY_SELECTED, {
        facility_id: facility?.id,
      });
    }
    return this.facility;
  }
}
